import { useRolodexEntries } from 'features/rolodex'
import first from 'lodash/first'
import get from 'lodash/get'
import isNil from 'lodash/isNil'
import reduce from 'lodash/reduce'
import { useMemo } from 'react'
import { SWRResponse } from 'swr'
import { GeckoEntry, RolodexEntryType } from 'types'
import { z } from 'zod'

import {
  EnrichedOntologyFieldDefinition,
  OntologyFieldType,
  OntologyTypeDefinition,
  OntologyV2,
  ontologyV2Schema,
} from './ontologyTypes'

export const FIELD_TYPE_DISPLAY_LABELS: ReadonlyArray<{
  value: OntologyFieldType
  label: string
}> = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'date', label: 'Date' },
  { value: 'object', label: 'Object' },
  { value: 'array', label: 'Array' },
]

export function extractValueFromEntry(
  entry: GeckoEntry,
  field: EnrichedOntologyFieldDefinition
): unknown {
  const [fieldFirstElm, ...fieldRestElms] = field.key.includes('.')
    ? field.key.split('.')
    : [field.key]

  let blob = {}
  if (field.keyOrField === 'field') {
    const key = fieldFirstElm as 'tags' | 'id' | 'data'
    blob = entry[key]
  } else {
    blob = entry.data[fieldFirstElm] ?? {}
  }

  const value = fieldRestElms.length
    ? get(blob, fieldRestElms.join('.'), undefined)
    : blob
  return value
}

export function ontologyFromEntry(entry: GeckoEntry): OntologyV2 {
  try {
    return ontologyV2Schema.parse(entry.data)
  } catch (error) {
    console.error(
      `Failed to parse ontology from entry ${entry.id}:`,
      error instanceof z.ZodError ? error.issues : error,
      "failed entry's data:",
      entry.data
    )
    return { types: {} }
  }
}

export function deriveIdFields(ontology: OntologyV2): OntologyV2 {
  const types = Object.fromEntries(
    Object.entries(ontology.types).map(([typeName, typeDef]) => [
      typeName,
      {
        ...typeDef,
        fields: {
          ...typeDef.fields,
          id: { name: `${typeDef.displayName} ID`, type: 'text' as const },
        },
      },
    ])
  )

  return { types }
}

export function convertKeyToLabel(key: string): string {
  return key
    .replace(/[-_]/g, ' ') // Replace '-' and '_' with spaces
    .split(' ') // Split the string into words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize the first letter of each word
    .join(' ') // Join the words back into a single string
}

/**
 * Loads organization-specific ontologies.
 * Loads all org-level ontologies as well as site-level ontologies, if on a site.
 * @param orgId the org ID to get configs for
 * @param siteId the site ID to get configs for
 * @returns the SWR for the loaded configs
 */
export function useLoadOrganizationOntology(
  orgId: string | undefined,
  siteId: string | undefined
): SWRResponse<ReadonlyArray<OntologyV2>> {
  // There is an assumption that site level ontologies are always tagged to the site's org,
  // so we only need to make one request of ontologies for the current org to get both org and site ontologies.
  const {
    entries: candidateEntries,
    isLoading,
    forceRefresh,
    isValidating,
    error,
  } = useRolodexEntries(
    useMemo(
      () =>
        orgId
          ? [
              {
                type: RolodexEntryType.ONTOLOGY,
                tags: { organization: orgId },
              },
            ]
          : [],
      [orgId]
    )
  )

  // If any entries are tagged to a site,
  // don't keep them unless we are on that exact site.
  const relevantOntologyEntries = useMemo(
    () =>
      candidateEntries.filter((e) => {
        const isOrgLevelOntology = isNil(e.tags['site'])
        const isMatchingSiteLevelOntology = siteId && e.tags['site'] === siteId
        return isOrgLevelOntology || isMatchingSiteLevelOntology
      }),
    [candidateEntries, siteId]
  )

  return useMemo(
    () => ({
      data: relevantOntologyEntries.map(ontologyFromEntry),
      isLoading: isLoading,
      mutate: async () => {
        await forceRefresh()
        return undefined
      },
      isValidating,
      error,
    }),
    [relevantOntologyEntries, error, forceRefresh, isLoading, isValidating]
  )
}

/**
 * Merge together two type definitions, in order - later type definitions will override
 * common properties of older ones.
 *
 * @param existing the existing type definition
 * @param incoming the incoming type definition
 * @returns the merged type definition
 */
const mergeTypeDef = (
  existing: OntologyTypeDefinition | undefined,
  incoming: OntologyTypeDefinition
): OntologyTypeDefinition => ({
  displayName: (incoming.displayName || existing?.displayName) ?? '',
  pluralName: (incoming.pluralName || existing?.pluralName) ?? '',
  icon: incoming.icon || existing?.icon,
  color: (incoming.color || existing?.color) ?? '',
  titleProperty: (incoming.titleProperty || existing?.titleProperty) ?? '',
  requiredTags: incoming.requiredTags?.length
    ? incoming.requiredTags
    : (existing?.requiredTags ?? []),
  fields: { ...existing?.fields, ...incoming.fields },
})

/**
 * Merge together multiple ontologies, in order - later ontologies will override
 * common properties of older ones.
 *
 * @param ontologies the configs to merge
 * @returns a single, combined ontology
 */
export const mergeOntologies = (
  ...ontologies: ReadonlyArray<OntologyV2>
): OntologyV2 =>
  reduce(
    ontologies,
    (acc, curr) => ({
      types: reduce(
        curr.types,
        (typeAcc, currTypeDef, typeKey) => ({
          ...typeAcc,
          [typeKey]: mergeTypeDef(typeAcc[typeKey], currTypeDef),
        }),
        acc.types
      ),
    }),
    { types: {} } as OntologyV2
  )

/**
 * Validate the merged ontology structure for consistency
 */
export function validateMergedOntology(config: OntologyV2) {
  const tagGraph = Object.fromEntries(
    Object.entries(config.types).map(([type, typeDef]) => [
      type,
      typeDef.requiredTags,
    ])
  ) as Record<string, ReadonlyArray<string>>

  validateTypeGraph(tagGraph)
  validateTransitiveClosure(tagGraph)

  // Validate that all types have proper definitions
  Object.entries(config.types).forEach(([type, typeDef]) => {
    if (!typeDef.displayName || !typeDef.pluralName || !typeDef.icon) {
      console.error(`Type ${type} is missing required properties`)
    }
  })
}

function validateTypeGraph(typeGraph: Record<string, ReadonlyArray<string>>) {
  const allTypes = new Set(
    Object.entries(typeGraph).flatMap(([type, requiredTags]) => [
      type,
      ...requiredTags,
    ])
  )
  // Ensure every type is connected
  allTypes.forEach((from) => {
    allTypes.forEach((to) => {
      if (from === to) {
        return
      }
      if (getInflectionType([from], [to], typeGraph) === null) {
        console.error(
          `Config error in Drilldown - disconnected type! No route between [${from}] and [${to}]`
        )
      }
    })
  })
}

/**
 * Validate that, for every type in the graph, it contains
 * the tags of all of its ancestors. That is, we have transitivity
 * in the tags going upward: "If unit is tagged to site, and site
 * is tagged to organization, then unit is also tagged to organization".
 * @param tagGraph graph to verify
 */
function validateTransitiveClosure(
  tagGraph: Record<string, ReadonlyArray<string>>
) {
  const errors: string[] = []

  // Helper function to recursively collect all ancestors
  function collectAncestors(
    type: string,
    visited = new Set<string>()
  ): Set<string> {
    const directAncestors = tagGraph[type] || []
    for (const ancestor of directAncestors) {
      if (!visited.has(ancestor)) {
        visited.add(ancestor)
        collectAncestors(ancestor, visited)
      }
    }
    return visited
  }

  // Check each type
  for (const type of Object.keys(tagGraph)) {
    const expectedAncestors = collectAncestors(type)
    const actualAncestors = new Set(tagGraph[type])

    // Check if actual ancestors match expected ancestors
    for (const ancestor of expectedAncestors) {
      if (!actualAncestors.has(ancestor)) {
        errors.push(
          `Type "${type}" is missing ancestor "${ancestor}" in its transitive closure.`
        )
      }
    }

    // Check for extra ancestors that shouldn't be there
    for (const ancestor of actualAncestors) {
      if (!expectedAncestors.has(ancestor)) {
        errors.push(
          `Type "${type}" incorrectly includes "${ancestor}" as an ancestor.`
        )
      }
    }
  }
  errors.forEach((err) => console.error(`Drilldown config error: ${err}`))
}

/**
 * Find the inflection type (common ancestor) between two branches of types
 */
export function getInflectionType(
  branchOne: ReadonlyArray<string>,
  branchTwo: ReadonlyArray<string>,
  tagGraph: Record<string, ReadonlyArray<string>>
): string | null {
  const commonAncestors = Object.keys(tagGraph).filter(
    (type) =>
      branchOne.some((filterType) =>
        isDescendant(filterType, type, tagGraph)
      ) &&
      branchTwo.some((filterType) => isDescendant(filterType, type, tagGraph))
  )
  return findLowestType(commonAncestors, tagGraph)
}

/**
 * Group types into buckets based on their hierarchical relationships
 */
export function bucketLinkedTypes(
  types: ReadonlyArray<string>,
  tagGraph: Record<string, ReadonlyArray<string>>
): ReadonlyArray<ReadonlyArray<string>> {
  const buckets: string[][] = []
  types.forEach((t) => {
    const matchingBucket = buckets.find((bucket) =>
      bucket.every((item) => {
        return (
          isDescendant(item, t, tagGraph) || isDescendant(t, item, tagGraph)
        )
      })
    )
    if (matchingBucket) {
      matchingBucket.push(t)
    } else {
      buckets.push([t])
    }
  })
  return buckets
}

/**
 * Determine whether a type is a descendant of some other type (or is the same type).
 *
 * @param type to check whether it is a descendant
 * @param ancestor to check whenther type is a descendant of
 * @returns true, if 'type' is a descendant of 'ancestor', or equal, false otherwise
 */
export function isDescendant(
  type: string,
  ancestor: string,
  tagGraph: Record<string, ReadonlyArray<string>>
): boolean {
  if (type === ancestor) return true
  const requiredTags = tagGraph[type]
  return requiredTags?.includes(ancestor) ?? false
}

/**
 * Find the lowest (most specific) type from a collection of types
 */
export function findLowestType(
  types: ReadonlyArray<string>,
  tagGraph: Record<string, ReadonlyArray<string>>
): string | null {
  const candidates = findAllLowestTypes(types, tagGraph)
  return first(candidates.toSorted()) ?? null
}

function findAllLowestTypes(
  types: ReadonlyArray<string>,
  tagGraph: Record<string, ReadonlyArray<string>>
): string[] {
  return types.filter((candidate) =>
    types.every(
      (otherType) =>
        candidate === otherType || !isDescendant(otherType, candidate, tagGraph)
    )
  )
}

export function toTitleCase(input: string): string {
  // Replace hyphens and underscores with spaces
  const cleanedInput = input.replace(/[-_]/g, ' ')

  // Split the string into words, capitalize each word, and join them back together
  const titleCased = cleanedInput
    .split(' ')
    .map((word) => {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    })
    .join(' ')

  return titleCased
}
